import { useEffect, useState } from 'react';
import type { Reveal } from '@/stores/roomStore';

/**
 * 랜덤 선택 애니메이션: 후보들을 빠르게 순회하다가 서버가 정한 결과에서 멈춥니다.
 * 결과는 이미 서버에서 확정되어 있으므로 모든 사용자가 같은 항목에서 멈춥니다.
 */
export function useRandomReveal(reveal: Reveal | null): { highlightId: string | null; landed: boolean } {
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const [landed, setLanded] = useState(false);

  useEffect(() => {
    if (!reveal) {
      setHighlightId(null);
      setLanded(false);
      return;
    }
    const { optionIds, optionId, durationMs, isRandom } = reveal;
    const timers: ReturnType<typeof setTimeout>[] = [];
    const landAt = Math.max(0, durationMs - 250);

    if (!isRandom || optionIds.length <= 1 || durationMs < 600) {
      setHighlightId(optionId);
      setLanded(true);
      return;
    }

    setLanded(false);
    let elapsed = 0;
    let delay = 70;
    let index = Math.max(0, optionIds.indexOf(optionId));
    const steps: Array<{ at: number; id: string }> = [];
    while (elapsed + delay < landAt) {
      index = (index + 1 + Math.floor(Math.random() * Math.max(1, optionIds.length - 1))) % optionIds.length;
      elapsed += delay;
      steps.push({ at: elapsed, id: optionIds[index] as string });
      delay = Math.min(260, delay * 1.11);
    }
    for (const step of steps) timers.push(setTimeout(() => setHighlightId(step.id), step.at));
    timers.push(
      setTimeout(() => {
        setHighlightId(optionId);
        setLanded(true);
      }, landAt),
    );
    return () => timers.forEach(clearTimeout);
  }, [reveal]);

  return { highlightId, landed };
}
